import {
  React,
  NavLink,
  logo,
  Layers,
  Users,
  UserRound,
  Briefcase,
  User,
  ChevronDown,
  useState,
  useLocation,
  useNavigate,
} from '../../utils/globalImports';
import { RootState } from '../../store';
import { ExtendedAccountResponse } from '../../store/types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Network, Menu, Tags, BotMessageSquare } from 'lucide-react';

interface MenuItem {
  name: string;
  path: string;
  icon: React.FC<any>;
  role?: string[];
  subMenu?: SubMenuItem[];
}

interface SubMenuItem {
  name: string;
  path: string;
  icon: React.FC<any>;
  role?: string[];
  subMenu?: SubMenuItem[];
}

interface Props {
  visible: boolean;
  collapsed?: boolean;
  onCollapseToggle?: () => void;
}

const CustomerSidebar: React.FC<Props> = ({ visible, collapsed = false, onCollapseToggle }) => {
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const rawRole = account?.user?.role || account?.role;
  const userRole = typeof rawRole === 'string' ? rawRole.trim().toLowerCase() : undefined;

  const toggleSubMenu = (itemName: string) => {
    setOpenSubMenus((prev) => {
      const newState = prev[itemName] === null ? false : !prev[itemName];
      if (newState === false) {
        const findPath = (items: MenuItem[] | SubMenuItem[]): string | null => {
          for (const item of items) {
            if (item.name === itemName) return item.path;
            if (item.subMenu) {
              const subPath = findPath(item.subMenu);
              if (subPath) return subPath;
            }
          }
          return null;
        };
        const path = findPath(menuItems);
        if (path) navigate(path);
      }
      return { ...prev, [itemName]: newState };
    });
  };

  const isSubMenuActive = (subMenu: SubMenuItem[] | undefined): boolean => {
    if (!subMenu) return false;
    return subMenu.some((subItem) => {
      if (subItem.path === location.pathname) return true;
      if (subItem.subMenu) {
        return subItem.subMenu.some((nestedItem) => location.pathname.startsWith(nestedItem.path));
      }
      return false;
    });
  };

  const getItemActiveClass = (itemPath: string): string => {
    // For SDK, match all /sdk/* routes
    if (itemPath === '/sdk/integrations') {
      return location.pathname.startsWith('/sdk') ? 'sub-active-link' : '';
    }
    // For others, exact match or startsWith base path
    return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/') ? 'sub-active-link' : '';
  };

  const menuItems: MenuItem[] =
    userRole === 'clientadmin'
      ? [
          {
            name: t('client.label'),
            path: '/client-management',
            icon: Briefcase,
            subMenu: [
              {
                name: t('group.label'),
                path: '/group-management',
                icon: User,
              },
              {
                name: t('category.label'),
                path: '/category-management',
                icon: Layers,
              },
            ],
          },
          {
            name: t('user.label'),
            path: '/user-management',
            icon: Users,
          },
          {
            name: t('sdk.label'),
            path: '/sdk/integrations',
            icon: BotMessageSquare,
          },
        ]
      : [
          {
            name: t('customer.label'),
            path: '/customer-management',
            icon: Network,
            role: ['superadmin', 'customeradmin'],
            subMenu: [
              {
                name: t('client.label'),
                path: '/client-management',
                icon: Briefcase,
                role: ['superadmin', 'customeradmin', 'clientadmin'],
                subMenu: [
                  {
                    name: t('group.label'),
                    path: '/group-management',
                    icon: Users,
                    role: ['superadmin', 'customeradmin', 'clientadmin'],
                  },
                  {
                    name: t('category.label'),
                    path: '/category-management',
                    icon: Tags,
                    role: ['superadmin', 'customeradmin', 'clientadmin'],
                  },
                ],
              },
            ],
          },
          {
            name: t('user.label'),
            path: '/user-management',
            icon: User,
            role: ['superadmin', 'customeradmin', 'clientadmin', 'user'],
          },
          {
            name: t('sdk.label'),
            path: '/sdk/integrations',
            icon: BotMessageSquare,
            role: ['superadmin', 'customeradmin', 'clientadmin'],
          },
        ];

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.role && userRole) {
      return item.role.includes(userRole);
    }
    return !item.role;
  });

  const initialOpenMenus = menuItems.reduce(
    (acc, item) => {
      acc[item.name] = true;
      if (item.subMenu) {
        item.subMenu.forEach((sub) => {
          acc[sub.name] = true;
        });
      }
      return acc;
    },
    {} as { [key: string]: boolean | null },
  );

  const [openSubMenus, setOpenSubMenus] = useState(initialOpenMenus);

  return (
    <div
      className={`customer-sidebar ${visible ? 'slide-in' : 'slide-out'} ${
        userRole === 'clientadmin' ? 'client-admin' : ''
      }${collapsed ? ' collapsed' : ''}`}
    >
      <div className="sidebar-logo-container">
        {!collapsed && <img src={logo} alt="Logo" className="sidebar-logo" />}
        <button type="button" className="sidebar-collapse-btn" onClick={onCollapseToggle}>
          <Menu size={20} />
        </button>
      </div>

      {filteredMenuItems.map((item, index) => {
        const isFirstLevelOpen = openSubMenus[item.name] ?? true;

        return (
          <div key={index} className="sidebar-item">
            <div className="sidebar-link-with-toggle">
              <NavLink to={item.path} className={() => getItemActiveClass(item.path)}>
                <div className="navlink-content">
                  <item.icon className="sub-sidebar-icon" />
                  {/* hide label when collapsed */}
                  {!collapsed && <span>{item.name}</span>}
                </div>
              </NavLink>

              {/* hide chevron when collapsed */}
              {!collapsed && item.subMenu && (
                <div className="chevron-wrapper" onClick={() => toggleSubMenu(item.name)}>
                  <ChevronDown className={`chevron ${isFirstLevelOpen ? 'rotate' : ''}`} />
                </div>
              )}
            </div>

            {/* First-level submenu: SHOW ALWAYS in collapsed mode */}
            {item.subMenu && (
              <div className={`submenu-wrapper ${isFirstLevelOpen || collapsed ? 'open' : ''}`}>
                {item.subMenu
                  .filter((subItem) => {
                    if (subItem.role && userRole) return subItem.role.includes(userRole);
                    return !subItem.role;
                  })
                  .map((subItem) => {
                    const isSecondLevelOpen =
                      collapsed ||
                      (openSubMenus[subItem.name] !== null
                        ? openSubMenus[subItem.name]
                        : isSubMenuActive(subItem.subMenu));

                    return (
                      <div key={subItem.name} className="submenu-item">
                        <div className="submenu-link-with-toggle">
                          <NavLink
                            to={subItem.path}
                            className={({ isActive }) => (isActive ? 'submenu-link sub-active-link' : 'submenu-link')}
                          >
                            <div className="navlink-content">
                              <subItem.icon className="submenu-icon" />
                              {!collapsed && <span>{subItem.name}</span>}
                            </div>
                          </NavLink>

                          {/* Second level chevron - only non-collapsed */}
                          {!collapsed && subItem.subMenu && (
                            <div className="chevron-wrapper" onClick={() => toggleSubMenu(subItem.name)}>
                              <ChevronDown className={`chevron ${isSecondLevelOpen ? 'rotate' : ''}`} />
                            </div>
                          )}
                        </div>

                        {/* Nested submenu: SHOW ALWAYS in collapsed */}
                        {subItem.subMenu && (
                          <div className={`nested-submenu-wrapper ${isSecondLevelOpen || collapsed ? 'open' : ''}`}>
                            {subItem.subMenu
                              .filter((nestedItem) => {
                                if (nestedItem.role && userRole) return nestedItem.role.includes(userRole);
                                return true;
                              })
                              .map((nestedItem) => (
                                <NavLink
                                  key={nestedItem.name}
                                  to={nestedItem.path}
                                  className={({ isActive }) =>
                                    isActive ? 'nested-submenu-link sub-active-link' : 'nested-submenu-link'
                                  }
                                >
                                  <div className="nested-nav-content">
                                    <nestedItem.icon className="nested-submenu-icon" />
                                    <span>{nestedItem.name}</span>
                                  </div>
                                </NavLink>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CustomerSidebar;
